import { Suspense, useEffect, useMemo, useState } from "react";
import { FreyrLibraryProvider } from "@freyr/freyr-react-common";
import { isExpired } from "react-jwt";
import { Outlet, useLocation } from "react-router-dom";
import cookie from "react-cookies";
import {
  ThemeProvider,
  createTheme,
  // useTheme
} from "@mui/material/styles";
import { Box, Stack } from "@mui/material";
import * as coreLocales from "@mui/material/locale";
import * as gridLocales from "@mui/x-data-grid/locales";
import * as pickersLocales from "@mui/x-date-pickers/locales";
import { apiGetDomains } from "../api/systemLibrary";
import { useGlobalData } from "../hooks/useGlobalData";
import { getURLDomain, logoutFunction } from "../common/utils";
import { LANGUAGEMAP, RIMS_COOKIE_CONSTANTS } from "../constants";
import useDomainStore from "../store/domain";
import { useConfigStore } from "../store/config";
import SideMenu from "../component/SideMenu";
import "../assets/scss/page/index.scss";
import { useUrfReceiveEvents } from "../hooks/urfEvents";
import { useQuery } from "@tanstack/react-query";
import { apiGetFeatureFlagListAll } from "../api/pm";
import { useFeatureFlagStore } from "../store/featureFlagStore";

type SupportedLocales = keyof typeof coreLocales;
type GridLocales = keyof typeof gridLocales;
type PickersLocales = keyof typeof pickersLocales;

const Layouts = () => {
  const [auth, setAuth] = useState<boolean>(false);
  const { token, tenant, domain: cookieDomain } = useGlobalData();
  const setFlags = useFeatureFlagStore((s) => s.setFlags);
  const { language } = useConfigStore();
  const URLDomain = getURLDomain();
  const redirectURL = `https://portal.${URLDomain}`;
  const { updateDomain, domainValue } = useDomainStore();
  const location = useLocation();
  const isBpmnRoute = location.pathname.startsWith("/bpmn");
  const [isDomainListLoading, setIsDomainListLoading] = useState<boolean>(true);
  const themeWithLocale = useMemo(() => {
    const locale: SupportedLocales = LANGUAGEMAP.mui[language];
    const gridLocale: GridLocales = LANGUAGEMAP.mui[language];
    const pickersLocale: PickersLocales = LANGUAGEMAP.mui[language];
    return createTheme(
      {
        palette: {},
      },
      coreLocales[locale],
      gridLocales[gridLocale],
      pickersLocales[pickersLocale]
    );
  }, [language]);

  useUrfReceiveEvents();

  useEffect(() => {
    let blLogin = token;
    if (blLogin === null || isExpired(blLogin)) {
      logoutFunction();
      window.location.href = `${redirectURL}?redirect=${window.location.origin}`;
    } else {
      setAuth(true);
    }
  }, [redirectURL, token]);

  useEffect(() => {
    // getDomainID
    if(!cookieDomain) {
      updateDomain({
        domainName: "ALL",
        domainId: "-1",
      });
      setIsDomainListLoading(false);
      return;
    }
    apiGetDomains().then((res) => {
        const result = res?.data;
        if (Array.isArray(result) && result.length > 0) {
        const curDomain = result.find((o) => o.domainPhrase === cookieDomain);
          if (curDomain) {
            updateDomain({
              domainName: curDomain?.domainPhrase,
              domainId: curDomain?.value,
            });
          }
        }
    },() => {})
    .finally(() => setIsDomainListLoading(false));
  }, []);

  const { data: featureFlagList, isFetching: featureFlagIsFetching } =
    useQuery(
      ["fetchFeatureFlagListAll"],
      () => {
        return apiGetFeatureFlagListAll();
      },
      {
        enabled: true,
        retry: false,
        refetchOnWindowFocus: false,
        select: (response) => {
          const result = response?.data;
          return result || {};
        },
      }
    );
    
  useEffect(() => {
    if (featureFlagList) {
      setFlags(featureFlagList);
    }
  }, [featureFlagList, setFlags]);
  
  const isReady = auth && !isDomainListLoading && !featureFlagIsFetching;
  
  return (
    <>
      {isReady && (
        <ThemeProvider theme={themeWithLocale}>
          <FreyrLibraryProvider
            token={token}
            language={language}
            tenant={isBpmnRoute ? (cookie.load(RIMS_COOKIE_CONSTANTS.AC_TENANT_ID) || tenant) : tenant}
            domain={cookieDomain ? domainValue.domainId : undefined}
            domainName={cookieDomain ? domainValue.domainName : undefined}
          >
            <Box className="custom-page" data-testid={"custom-page"}>
                <Suspense>
                  <Outlet />
                </Suspense>
            </Box>
          </FreyrLibraryProvider>
        </ThemeProvider>
      )}
    </>
  );
};

export default Layouts;
